import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Seo from './Seo';
import { useLocation } from 'react-router-dom';
import { localizedPath, urlLanguage } from '../content/publicLanguages';
import PublicNavbar from './PublicNavbar';

// Photos and screenshots are served as WebP: about a tenth of the PNG weight, which matters on the
// mobile data most farmers use (and page speed counts for search ranking). The originals stay in
// public/ for social previews (og:image).
const WEBP = new Set(['climate-resilience.jpg', 'crop-diagnosis-fr.png', 'crop-diagnosis-response.png', 'crop-diagnosis.png',
  'farmers-field.png', 'field-advisor.png', 'resilience-pillars-es.png', 'resilience-pillars-fr.png', 'resilience-pillars.png',
  'soil-analysis-response.png', 'soil-life.jpg', 'soil-report-fr.png', 'soil-report.png', 'technical-guidance-response-fr.png',
  'technical-guidance-response.png']);
const asset = (path) => `/${WEBP.has(path) ? path.replace(/\.(png|jpe?g)$/i, '.webp') : path}`;

const IconFinance = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 17 L8 11 L13 14 L21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 6h4v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="3" y1="21" x2="21" y2="21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconTiming = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="1.8" />
    <path d="M14 8v6l4 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 3v2M25 14h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconLocalConditions = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M14 24s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="14" cy="12" r="2.3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M18 5.5c1.5-1 3.2-.8 4.2.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconProject = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M4 8.5a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M14 12v6M11 15h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const IconWhatsApp = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M14 3.5a10.5 10.5 0 0 0-9.1 15.7L3.7 24.5l5.5-1.2A10.5 10.5 0 1 0 14 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M10.2 9.2c.3-.4.7-.4 1-.1l1.2 1.5c.2.3.2.6 0 .9l-.6.7c.6 1.2 1.5 2.1 2.7 2.7l.7-.6c.3-.2.6-.2.9 0l1.5 1.2c.3.3.3.7-.1 1-1 .9-2.3 1-3.5.5a9 9 0 0 1-4.2-4.2c-.5-1.2-.4-2.5.4-3.6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isEnglish = i18n.language?.startsWith('en');

  const questions = t('landing.questions', { returnObjects: true });
  const whyIconSet = [IconTiming, IconLocalConditions, IconProject, IconFinance, IconWhatsApp];
  const whyItems = whyIconSet.map((Icon, index) => ({
    Icon,
    ...t(`landing.why.items.${index}`, { returnObjects: true }),
  }));
  const featureImages = [
    asset(i18n.language?.startsWith('fr') ? 'technical-guidance-response-fr.png' : 'technical-guidance-response.png'),
    asset('crop-diagnosis.png'),
    asset(i18n.language?.startsWith('fr') ? 'soil-report-fr.png' : 'soil-report.png'),
  ];
  const features = featureImages.map((img, index) => ({
    num: `0${index + 1}`,
    img,
    ...t(`landing.features.items.${index}`, { returnObjects: true }),
  }));
  const audiences = [0, 1, 2, 3].map((index) => (
    t(`landing.audience.items.${index}`, { returnObjects: true })
  ));
  const financeSteps = t('landing.finance.steps', { returnObjects: true });
  const financeOutcomes = t('landing.finance.outcomes', { returnObjects: true });
  const plans = t('landing.pricing.plans', { returnObjects: true });

  useEffect(() => {
    document.body.classList.add('public-page-body');

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal').forEach((element) => revealObserver.observe(element));

    return () => {
      document.body.classList.remove('public-page-body');
      revealObserver.disconnect();
    };
  }, []);

  const getQuestions = (rowIndex) => {
    if (rowIndex === 0) {
      return questions;
    }

    const half = Math.floor(questions.length / 2);
    return [...questions.slice(half), ...questions.slice(0, half)];
  };

  return (
    <>
      <Seo
        title={t('landing.seo.title')}
        description={t('landing.seo.description')}
        canonicalPath={localizedPath('/', urlLanguage(location.pathname))}
      />

      <div className="landing-root">
        <PublicNavbar />

        <section className="hero-section">
          <div className="hero-bg-blob" aria-hidden="true" />
          <div className="hero-bg-blob hero-bg-blob--2" aria-hidden="true" />
          <svg className="hero-field-svg" viewBox="0 0 600 400" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#8FBFB6" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#7DADA4" stopOpacity="0.04" />
              </linearGradient>
            </defs>
            <ellipse cx="480" cy="80" rx="200" ry="180" fill="url(#g1)" />
            <ellipse cx="100" cy="320" rx="160" ry="120" fill="url(#g1)" />
          </svg>

          <div className="hero-inner">
            <div className="hero-left">
              <div className="hero-badge hero-a hero-a-1">
                <span className="badge-dot" />
                {t('landing.hero.badge')}
              </div>

              <h1 className="hero-title hero-a hero-a-2">
                {t('landing.hero.title_line1')}
                <br />
                <em>{t('landing.hero.title_line2')}</em>
              </h1>

              <p className="hero-subtitle hero-a hero-a-3">
                <>
                  <strong>{t('landing.hero.subtitle_lead')}</strong>
                  <br />
                  {t('landing.hero.subtitle_body')}
                </>
              </p>

              <div className="hero-proof hero-a hero-a-4">
                <span className="proof-text">
                  {t('landing.hero.proof_prefix')} <strong>{t('landing.hero.proof_highlight')}</strong>
                </span>
              </div>

              <div className="hero-actions hero-a hero-a-4">
                <a href="/login" className="cta-primary">
                  {t('landing.hero.cta')} <span className="cta-arrow">&rarr;</span>
                </a>
              </div>
            </div>

            <div className="hero-right hero-a hero-a-3">
              <div className="hero-img-frame">
                <img src={asset('Agroforestry.jpg')} alt={t('landing.hero.image_alt')} className="hero-img" />
                <div className="hero-img-overlay" aria-hidden="true" />
              </div>

              <div className="hero-chat-card">
                <div className="chat-card-header">
                  <img src={asset('Logo.png')} alt="" className="chat-logo" />
                  <span>UpyaChat</span>
                  <span className="chat-online-dot" />
                </div>
                <div className="chat-bubble chat-bubble--user">{t('landing.hero.chat_question')}</div>
                <div className="chat-bubble chat-bubble--ai">
                  <span className="typing-dots"><span /><span /><span /></span>
                  {t('landing.hero.chat_answer')}
                </div>
              </div>

              <div className="hero-deco-ring" aria-hidden="true" />
              <div className="hero-deco-dot" aria-hidden="true" />
            </div>
          </div>

          <div className="hero-scroll-cue" aria-hidden="true">
            <span>{t('landing.hero.scroll')}</span>
            <div className="scroll-line" />
          </div>
        </section>

        <section className="marquee-section">
          <div className="marquee-label">{t('landing.marquee.label')}</div>
          <div className="marquee-track-wrap">
            {[0].map((rowIndex) => (
              <div key={rowIndex} className={`marquee-row${rowIndex === 1 ? ' marquee-row--reverse' : ''}`}>
                {[0, 1].map((clone) => (
                  <div key={`${rowIndex}-${clone}`} className="marquee-group" aria-hidden={clone === 1 ? 'true' : undefined}>
                    {getQuestions(rowIndex).map((question, index) => (
                      <div className="marquee-pill" key={`${rowIndex}-${clone}-${index}`}>
                        <span className="pill-star">&#10022;</span>
                        {question}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section
          className="resilience-section reveal"
          style={{ '--landing-resilience-image': `url(${asset('climate-resilience.jpg')})` }}
        >
          <div className="resilience-inner">
            <span className="section-tag">{t('landing.resilience.tag')}</span>
            <h2>{t('landing.resilience.title')}</h2>
            <p>{t('landing.resilience.description')}</p>
          </div>
        </section>

        {(isEnglish || i18n.language?.startsWith('fr') || i18n.language?.startsWith('es')) && (
          <div className="resilience-pillars reveal">
            <img
              src={asset(
                i18n.language?.startsWith('fr')
                  ? 'resilience-pillars-fr.png'
                  : i18n.language?.startsWith('es')
                    ? 'resilience-pillars-es.png'
                    : 'resilience-pillars.png'
              )}
              alt={i18n.language?.startsWith('fr')
                ? 'Moins d’intrants, meilleures pratiques et plus de résilience'
                : i18n.language?.startsWith('es')
                  ? 'Menor dependencia de insumos, mejores prácticas y mayor resiliencia'
                  : 'Lower input dependency, improve practices, and secure resilience'}
            />
          </div>
        )}

        <section className="why-section" id="why">
          <div className="why-intro reveal">
            <div className="section-header">
              <span className="section-tag">{t('landing.why.tag')}</span>
              <h2>{t('landing.why.title')}</h2>
            </div>
            <div className="why-intro-media">
              <img
                src={asset('field-advisor.png')}
                alt={t('landing.why.image_alt', {
                  defaultValue: 'A farmer and an agricultural advisor reviewing field conditions together',
                })}
                loading="lazy"
              />
            </div>
          </div>
          <div className="why-grid">
            {whyItems.map(({ Icon, title, desc }, index) => (
              <div className={`why-card reveal reveal-d${index + 1}`} key={title}>
                <div className="why-card-top">
                  <div className="why-icon-wrap"><Icon /></div>
                  <span className="why-num">0{index + 1}</span>
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="features-section" id="how">
          <div className="section-header reveal">
            <span className="section-tag">{t('landing.features.tag')}</span>
            <h2>{t('landing.features.title')}</h2>
          </div>

          {features.map((feature, index) => (
            <div
              className={`feature-block reveal${index % 2 === 1 ? ' feature-block--flip' : ''}${index === 1 ? ' feature-block--diagnosis' : ''}${index === 2 ? ' feature-block--soil-report' : ''}`}
              key={feature.num}
            >
              <div className="feature-text">
                <span className="feature-num">{feature.num}</span>
                <h3>{feature.title}</h3>
                <p className="feature-desc">{feature.desc}</p>
                {index === 1 && (
                  <div className="feature-response">
                    <img
                      src={asset(i18n.language?.startsWith('fr') ? 'crop-diagnosis-fr.png' : 'crop-diagnosis-response.png')}
                      alt={i18n.language?.startsWith('fr')
                        ? 'UpyaChat diagnostiquant un problème sur des plants de maïs'
                        : 'UpyaChat visual diagnosis response for a maize ear'}
                      loading="lazy"
                    />
                  </div>
                )}
                {index === 2 && (
                  <div className="feature-soil-photo feature-soil-photo--inline">
                    <img
                      src={asset('soil-life.jpg')}
                      alt="Earthworms and organic matter in healthy soil"
                      loading="lazy"
                    />
                  </div>
                )}
                <a href="/login" className="feature-link">
                  {t('landing.features.cta')} <span className="cta-arrow">&rarr;</span>
                </a>
              </div>
              {index === 2 ? (
                <div className="feature-soil-response feature-soil-response--standalone">
                  <img
                    src={asset(i18n.language?.startsWith('fr') ? 'soil-report-fr.png' : 'soil-analysis-response.png')}
                    alt={i18n.language?.startsWith('fr')
                      ? 'UpyaChat interprétant une analyse de sol et recommandant du phosphore et des oligo-éléments'
                      : 'UpyaChat interpreting a soil analysis and recommending phosphorus and micronutrients'}
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className={`feature-img-wrap feature-img-wrap--${index + 1}`}>
                  <img
                    src={feature.img}
                    alt={feature.alt}
                    className={`feature-img feature-img--${index + 1}`}
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          ))}
        </section>

        <section className="audience-section reveal">
          <div className="section-header">
            <span className="section-tag">{t('landing.audience.tag')}</span>
            <h2>{t('landing.audience.title')}</h2>
          </div>
          <div className="audience-feature reveal">
            <img
              src={asset('farmers-field.png')}
              alt={t('landing.audience.image_alt', {
                defaultValue: 'Farmers working together in a field',
              })}
              loading="lazy"
            />
          </div>
          <div className="audience-grid">
            {audiences.map((audience) => (
              <div className="audience-card" key={audience.label}>
                <h3>{audience.label}</h3>
                <p>{audience.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="finance-section reveal" id="finance">
          <div className="finance-section-inner">
            <div className="finance-intro">
              <span className="section-tag section-tag--finance">{t('landing.finance.tag')}</span>
              <h2>{t('landing.finance.title')}</h2>
              <p className="finance-lead">{t('landing.finance.description')}</p>
              <a href="/login" className="cta-primary finance-cta">
                {t('landing.finance.cta')} <span className="cta-arrow">&rarr;</span>
              </a>
            </div>

            <div className="finance-process" aria-label={t('landing.finance.process_label')}>
              <div className="finance-process-heading">
                <span className="finance-process-kicker">{t('landing.finance.process_kicker')}</span>
                <h3>{t('landing.finance.process_title')}</h3>
              </div>
              <div className="finance-steps">
                {financeSteps.map((step, index) => (
                  <div className="finance-step" key={step.title}>
                    <span className="finance-step-number">0{index + 1}</span>
                    <div>
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="finance-outcomes">
            {financeOutcomes.map((outcome) => (
              <div className="finance-outcome" key={outcome.title}>
                <span className="finance-outcome-mark" aria-hidden="true">&#10003;</span>
                <div>
                  <h3>{outcome.title}</h3>
                  <p>{outcome.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="section-header reveal">
            <span className="section-tag">{t('landing.pricing.tag')}</span>
            <h2>{t('landing.pricing.title')}</h2>
            <p className="section-sub">{t('landing.pricing.subtitle')}</p>
          </div>

          <div className="pricing-grid reveal">
            {plans.map((plan) => (
              <div className={`pricing-card${plan.highlight ? ' pricing-card--highlight' : ''}`} key={plan.name}>
                {plan.highlight && <div className="pricing-badge">{t('landing.pricing.badge')}</div>}
                <div className="pricing-top">
                  <span className="pricing-name">{plan.name}</span>
                  <span className="pricing-tagline">{plan.tagline}</span>
                </div>
                <div className="pricing-price">
                  <span className="pricing-amount">{plan.price}&#8364;</span>
                  <span className="pricing-period">{plan.period}</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((item) => (
                    <li key={item}>
                      <span className="check">&#10003;</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <a href="/login" className={plan.highlight ? 'cta-primary cta-full' : 'cta-outline cta-full'}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </section>

        <section className="final-cta reveal">
          <div className="final-cta-glow" aria-hidden="true" />
          <div className="final-cta-glow final-cta-glow--2" aria-hidden="true" />
          <svg className="final-cta-deco" viewBox="0 0 200 200" aria-hidden="true">
            <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <circle cx="100" cy="100" r="65" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            <path d="M100 40 Q120 70 100 100 Q80 70 100 40Z" fill="rgba(228,225,224,0.16)" />
            <path d="M100 100 Q130 120 140 160" stroke="rgba(228,225,224,0.24)" strokeWidth="2" fill="none" />
            <path d="M100 100 Q70 120 60 160" stroke="rgba(228,225,224,0.24)" strokeWidth="2" fill="none" />
          </svg>
          <div className="final-cta-inner">
            <span className="section-tag section-tag--light">{t('landing.final_cta.tag')}</span>
            <h2>
              {t('landing.final_cta.title_line1')}
              <br />
              <em>{t('landing.final_cta.title_line2')}</em>
            </h2>
            <p>{t('landing.final_cta.subtitle')}</p>
            <a href="/login" className="cta-primary cta-primary--inverted cta-large">
              {t('landing.final_cta.cta')} <span className="cta-arrow">&rarr;</span>
            </a>
          </div>
        </section>

        <footer className="footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <img src={asset('Logo.png')} alt="UpyaChat" className="navbar-logo" />
              <span className="navbar-name">UpyaChat</span>
            </div>
            <p className="footer-copy">{t('public.footer.copy')}</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
